import DateTimeFormatter from '@Components/DateTimeFormatter/DateTimeFormatter';
import MainTable from '@Components/MainTable';
import { MainTableRow } from '@Components/MainTable/MainTable';
import { onPageChangeRequestMeta } from '@Components/MainTable/MainTable.types';
import NoResults from '@Components/NoResults/NoResults';
import SidebarContentWrapper from '@Components/SidebarContentWrapper';
import { TableHeader } from '@Features/generics/generics.type';
import EnrollementModel from '@Pages/ClassRooms/Pages/SingleCourse/Components/EnrollmentModel/EnrollmentModel';
import UnEnrollementModel from '@Pages/ClassRooms/Pages/SingleCourse/Components/EnrollmentModel/UnEnroolmentModel';
import { useSnackbar } from '@Providers/useSnackbar';
import api from '@Services/api';
import { FlexLayout } from '@Styled/utilities';
import AddBoxSharpIcon from '@mui/icons-material/AddBoxSharp';
import IndeterminateCheckBoxOutlinedIcon from '@mui/icons-material/IndeterminateCheckBoxOutlined';
import { Button, Chip, TableCell, Tooltip } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { BanButton, StatusChip } from '../Students/StudentTabs/Subscriptions/subscriptions.shared';
import useStandalonePageLoader from './useStandalonePageLoader';
import useAdminActionPermissions from './useAdminActionPermissions';

type StandaloneCourseUsersProps = { courseId: number; price: number };

const TABLE_HEADS: TableHeader[] = [
	{ displayName: 'USERNAME', fieldName: 'username', width: '12%' },
	{ displayName: 'FIRST NAME', fieldName: 'first_name', width: '12%' },
	{ displayName: 'LAST NAME', fieldName: 'last_name', width: '12%' },
	{ displayName: 'PHONE', fieldName: 'phone_number', width: '13%' },
	{ displayName: 'METHOD', fieldName: 'purchase_method', width: '10%' },
	{ displayName: 'ENROLLED ON', fieldName: 'enrolled_on', width: '13%' },
	{ displayName: 'STATUS', fieldName: 'status', canSort: false, width: '10%' },
];

const USER_FILTERS = [
	{
		name: 'Enrollment Status',
		value: 'enrolled',
		type: 'dropdown',
		options: [
			{ name: 'Enrolled', value: true },
			{ name: 'Not Enrolled', value: false },
		],
	},
	{
		name: 'Method',
		value: 'purchase_method',
		type: 'dropdown',
		options: ['admin', 'instructor', 'wallet', 'scratchcard', 'free'].map(
			(value) => ({ name: value, value })
		),
	},
];

const apiData = <T,>(response: any): T => response.data.data as T;
const errorMessage = (error: any, fallback: string) =>
	error.response?.data?.message || error.data?.message || fallback;

const StandaloneCourseUsers: React.FC<StandaloneCourseUsersProps> = ({
	courseId,
	price,
}) => {
	const { displaySnackbar } = useSnackbar();
	const { withLoading } = useStandalonePageLoader();
	const isAdmin = api.defaults.baseURL?.includes('/admin/') === true;
	const { can } = useAdminActionPermissions();
	const canManage = can('u-std-mng-cr');
	const [users, setUsers] = useState<any[]>([]);
	const [meta, setMeta] = useState<any>({});
	const [lastRequest, setLastRequest] = useState<Partial<onPageChangeRequestMeta>>(
		{}
	);
	const [selectedUser, setSelectedUser] = useState<any>();
	const [openEnroll, setOpenEnroll] = useState(false);
	const [openUnenroll, setOpenUnenroll] = useState(false);
	const [deduct, setDeduct] = useState<boolean | undefined>();
	const [refund, setRefund] = useState<boolean | undefined>();

	const load = useCallback(
		async (request: Partial<onPageChangeRequestMeta> = {}) => {
			setLastRequest(request);
			try {
				await withLoading(async () => {
					const response = await api.post('standalone-courses/users', {
						standalone_course_id: courseId,
						page: request.activePage || 1,
						perPage: request.perPage || 10,
						filters: request.filters || [],
						query: request.query || '',
						// no default: unsorted loads use the backend's roster
						// ordering (enrolled first, newest enrollment first)
						sortBy: request.sortBy,
					});
					setUsers(apiData<any[]>(response));
					setMeta(response.data.meta || {});
				});
			} catch (error: any) {
				displaySnackbar(
					'error',
					errorMessage(error, 'Unable to load standalone Course users')
				);
			}
		},
		[courseId, displaySnackbar, withLoading]
	);

	useEffect(() => {
		load();
	}, [load]);

	const closeEnroll = () => {
		setOpenEnroll(false);
		setSelectedUser(undefined);
		setDeduct(undefined);
	};

	const closeUnenroll = () => {
		setOpenUnenroll(false);
		setSelectedUser(undefined);
		setRefund(undefined);
	};

	const enroll = async () => {
		if (!selectedUser) return;
		try {
			await withLoading(() =>
				api.post('standalone-courses/users/enroll', {
					standalone_course_id: courseId,
					user_id: selectedUser.id,
					...(isAdmin && price > 0 ? { deduct } : {}),
				})
			);
			displaySnackbar('success', 'Student enrolled successfully');
			closeEnroll();
			load(lastRequest);
		} catch (error: any) {
			displaySnackbar(
				'error',
				errorMessage(error, 'Unable to enroll student')
			);
		}
	};

	const unenroll = async () => {
		if (!selectedUser || !isAdmin) return;
		try {
			await withLoading(() =>
				api.delete('standalone-courses/users/remove', {
					data: {
						standalone_course_id: courseId,
						user_id: selectedUser.id,
						refund: Boolean(refund),
					},
				})
			);
			displaySnackbar(
				'success',
				refund
					? 'Enrollment removed and refund processed successfully'
					: 'Enrollment removed successfully'
			);
			closeUnenroll();
			load(lastRequest);
		} catch (error: any) {
			displaySnackbar(
				'error',
				errorMessage(error, 'Unable to remove enrollment')
			);
		}
	};

	// Available to BOTH portals: the instructor route
	// /standalone-courses/users/status is owner-scoped server-side.
	// Unenroll/refund stay admin-only below. The backend rejects rows managed
	// by a bundle (ban from the bundle's Users tab instead).
	const setStatus = async (user: any, active: boolean) => {
		try {
			await withLoading(() =>
				api.post('standalone-courses/users/status', {
					standalone_course_id: courseId,
					user_id: user.id,
					active,
				})
			);
			displaySnackbar('success', active ? 'Student unbanned' : 'Student banned — the student loses access to this course');
			load(lastRequest);
		} catch (error: any) {
			displaySnackbar('error', errorMessage(error, 'Unable to update enrollment status'));
		}
	};

	return (
		<SidebarContentWrapper>
			<EnrollementModel
				setDeduct={setDeduct}
				handleClose={closeEnroll}
				openEnrolled={openEnroll}
				onEnroll={enroll}
				showDeductChoices={isAdmin && price > 0}
				modelText="student"
				confirmationText={
					isAdmin && price > 0
						? 'Are you sure you want to enroll this student in this standalone Course?'
						: isAdmin
						? 'Are you sure you want to enroll this student in this free standalone Course? No wallet deduction or invoice will be created.'
						: 'Are you sure you want to assign this standalone Course to this student? No wallet deduction or invoice will be created.'
				}
			/>
			{isAdmin && (
				<UnEnrollementModel
					handleCloseUnEnrolledModel={closeUnenroll}
					onUNEnroll={unenroll}
					setRefund={setRefund}
					openUnEnrolled={openUnenroll}
					modelText="student"
					confirmationText={`Are you sure you want to unenroll this student from this standalone Course? The refundable historical amount is ${
						selectedUser?.refundable_amount || 0
					}. Related Lecture enrollments will also be removed.`}
				/>
			)}

			<MainTable
				onPageChangeRequest={load}
				tableFilters={USER_FILTERS}
				totalPages={Number(meta.last_page || 1)}
				total={Number(meta.total || users.length)}
				RowsperPage={Number(meta.per_page || 10)}
				pageNum={Number(meta.current_page || 1)}
				tableHeads={TABLE_HEADS}
				tableMinWidth={650}
				renderItems={() =>
					users.length === 0 ? (
						<MainTableRow>
							<TableCell colSpan={TABLE_HEADS.length}>
								<NoResults />
							</TableCell>
						</MainTableRow>
					) : (
						users.map((user) => (
							<MainTableRow key={user.id} hover sx={{ height: '52px' }}>
								<Cell>
									<Chip label={user.username || '-'} sx={{ fontSize: '13px' }} />
								</Cell>
								<Cell>{user.first_name || '-'}</Cell>
								<Cell>{user.last_name || '-'}</Cell>
								<Tooltip title={user.phone_number || '-'} placement="top">
									<Cell>{user.phone_number || '-'}</Cell>
								</Tooltip>
								<Cell>{user.purchase_method || '-'}</Cell>
								<Cell>
									{user.enrolled_on ? (
										<DateTimeFormatter date={user.enrolled_on} />
									) : (
										'-'
									)}
								</Cell>
								<Cell>
									{user.enrolled || user.active === false ? (
										<FlexLayout alignItems="center">
											{(!isAdmin || !canManage) && (
												<StatusChip status={user.active === false ? 'banned' : 'active'} />
											)}
											{canManage &&
												(user.active === false ? (
													<Tooltip title="Unban Student" placement="left">
														<BanButton banned onClick={() => setStatus(user, true)} />
													</Tooltip>
												) : (
													<Tooltip title="Ban Student" placement="left">
														<BanButton banned={false} onClick={() => setStatus(user, false)} />
													</Tooltip>
												))}
											{isAdmin && canManage && (
												<Tooltip title="Unenroll Student" placement="left">
													<Button
														onClick={() => {
															setSelectedUser(user);
															setOpenUnenroll(true);
														}}
													>
														<IndeterminateCheckBoxOutlinedIcon
															sx={{ color: '#5ac0fc', height: 27, width: 26 }}
														/>
													</Button>
												</Tooltip>
											)}
										</FlexLayout>
									) : canManage ? (
										<Tooltip title="Enroll Student" placement="left">
											<Button
												onClick={() => {
													setSelectedUser(user);
													setOpenEnroll(true);
												}}
											>
												<AddBoxSharpIcon
													sx={{ color: '#5ac0fc', height: 26, width: 26 }}
												/>
											</Button>
										</Tooltip>
									) : (
										'-'
									)}
								</Cell>
							</MainTableRow>
						))
					)
				}
			/>
		</SidebarContentWrapper>
	);
};

const Cell = styled(TableCell)`
	font-family: Roboto;
	font-size: 1rem;
	padding: 0 1rem;
	white-space: nowrap;
	text-overflow: ellipsis;
	overflow: hidden;
	max-width: 12rem;
`;

export default StandaloneCourseUsers;
